<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useProductsStore } from '@/stores/products'
import { categories } from '@/data/products'
import { getStockStatus, getStockText } from '@/utils/formatters'

const router = useRouter()
const productsStore = useProductsStore()
const activeCategory = ref('power')

// 在组件加载时获取产品列表
onMounted(() => {
  productsStore.fetchProducts()
})

// 根据分类筛选产品
const filteredProducts = computed(() => {
  return productsStore.products.filter(p => p.category === activeCategory.value)
})

// 申请样品
function handleApplySample(product) {
  router.push({ path: '/sample/apply', query: { productId: product.id } })
}

// 佣金计算
function handleCommissionCalc(product) {
  router.push({ path: '/commission/calc', query: { productId: product.id } })
}

function getDeliveryText(stock = {}) {
  return stock.deliveryTime || `${stock.deliveryDays || 1}天送达`
}
</script>

<template>
  <div class="page-container">
    <!-- 数据来源提示 -->
    <div v-if="productsStore.usingMockData" class="data-source-notice">
      <p>ℹ️ 当前使用模拟数据（API 不可用）</p>
    </div>

    <!-- 加载中状态 -->
    <div v-if="productsStore.loading" class="container loading-state">
      <div class="spinner"></div>
      <p>加载中...</p>
    </div>

    <div v-else class="container product-layout">
      <!-- Sidebar Navigation -->
      <aside class="sidebar">
        <h3 class="sidebar-title">产品分类</h3>
        <ul class="category-list">
          <li
            v-for="cat in categories"
            :key="cat.id"
            :class="{ active: activeCategory === cat.id }"
            @click="activeCategory = cat.id"
          >
            {{ cat.name }}
            <span class="arrow">&rsaquo;</span>
          </li>
        </ul>
      </aside>

      <!-- Main Content Area -->
      <main class="main-content">
        <div class="content-header">
          <h2>{{ categories.find(c => c.id === activeCategory)?.name }}</h2>
          <p class="result-count">共找到 {{ filteredProducts.length }} 款产品</p>
        </div>

        <!-- Product List Table -->
        <div class="product-list" v-if="filteredProducts.length > 0">
          <div class="product-list-header">
            <div class="col-product">产品信息</div>
            <div class="col-specs">关键参数</div>
            <div class="col-stock">库存状态</div>
            <div class="col-action">操作</div>
          </div>

          <!-- Product Rows -->
          <div
            v-for="product in filteredProducts"
            :key="product.id"
            class="product-list-item"
          >
            <!-- Product Info Column -->
            <div class="col-product">
              <div class="product-info">
                <img :src="product.image" :alt="product.name" class="product-thumb" />
                <div class="product-details">
                  <p class="brand">{{ product.brand }}</p>
                  <router-link
                    :to="`/products/${product.id}`"
                    class="product-name-link"
                  >
                    {{ product.name }}
                  </router-link>
                  <p class="model">型号: {{ product.model }}</p>
                  <div v-if="product.tag" class="tag-inline" :class="product.tag.toLowerCase()">
                    {{ product.tag }}
                  </div>
                </div>
              </div>
            </div>

            <!-- Specs Column -->
            <div class="col-specs">
              <ul class="specs-list">
                <li v-for="spec in product.specs" :key="spec.label">
                  <span class="spec-label">{{ spec.label }}:</span>
                  <span class="spec-value">{{ spec.value }}</span>
                </li>
              </ul>
            </div>

            <!-- Stock Status Column -->
            <div class="col-stock">
              <div class="stock-info">
                <span class="stock-count">库存: {{ product.stock.available }}件</span>
                <span
                  class="stock-status"
                  :class="`status-${getStockStatus(product.stock.available)}`"
                >
                  {{ getStockText(getStockStatus(product.stock.available)) }}
                </span>
                <span class="delivery-time">{{ getDeliveryText(product.stock) }}</span>
              </div>
            </div>

            <!-- Action Column -->
            <div class="col-action">
              <div class="action-content">
                <!-- Action Buttons/Links -->
                <div class="action-buttons">
                  <button class="btn-action btn-cart" @click="handleApplySample(product)" title="申请样品">
                    📦 申请样品
                  </button>
                  <router-link :to="`/products/${product.id}`" class="btn-action btn-link">
                    查看详情
                  </router-link>
                  <button class="btn-action btn-commission" @click="handleCommissionCalc(product)" title="佣金计算">
                    💰 佣金计算
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-else class="empty-state">
          该分类下暂无产品信息。
        </div>
      </main>
    </div>
  </div>
</template>

<style scoped>
.data-source-notice {
  background: #fff3cd;
  border-bottom: 2px solid #ffc107;
  padding: 12px 0;
  text-align: center;
  margin-bottom: 0;
}

.data-source-notice p {
  margin: 0;
  color: #856404;
  font-size: 13px;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 500px;
  text-align: center;
}

.loading-state .spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #005eb8;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.page-container {
  padding: 40px 0 80px;
  background-color: #f9f9f9;
}

.product-layout {
  display: flex;
  gap: 30px;
  align-items: flex-start;
}

/* Sidebar Styles */
.sidebar {
  width: 280px;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  flex-shrink: 0;
}

.sidebar-title {
  padding: 15px 20px;
  margin: 0;
  font-size: 16px;
  background-color: #f5f5f5;
  border-bottom: 1px solid #e0e0e0;
  color: #333;
}

.category-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.category-list li {
  padding: 12px 20px;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
  color: #555;
  transition: all 0.2s;
}

.category-list li:last-child {
  border-bottom: none;
}

.category-list li:hover {
  background-color: #f0f8ff;
  color: #005eb8;
}

.category-list li.active {
  background-color: #005eb8;
  color: white;
  border-color: #005eb8;
}

.category-list li.active .arrow {
  color: white;
}

/* Main Content Styles */
.main-content {
  flex-grow: 1;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 30px;
  min-height: 500px;
}

.content-header {
  border-bottom: 2px solid #f0f0f0;
  margin-bottom: 20px;
  padding-bottom: 10px;
}

.content-header h2 {
  margin: 0 0 10px 0;
  color: #1a1a1a;
  font-size: 24px;
}

.result-count {
  margin: 0;
  color: #888;
  font-size: 13px;
}

/* Product List Styles */
.product-list {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.product-list-header {
  display: grid;
  grid-template-columns: 1.6fr 1.3fr 1fr 1.2fr;
  gap: 20px;
  padding: 16px 20px;
  background: #f5f5f5;
  border: 1px solid #e0e0e0;
  border-radius: 4px 4px 0 0;
  font-weight: 600;
  color: #333;
  font-size: 13px;
}

.product-list-item {
  display: grid;
  grid-template-columns: 1.6fr 1.3fr 1fr 1.2fr;
  gap: 20px;
  padding: 20px;
  border: 1px solid #e0e0e0;
  border-top: none;
  align-items: flex-start;
  transition: background-color 0.2s;
}

.product-list-item:last-child {
  border-radius: 0 0 4px 4px;
}

.product-list-item:hover {
  background-color: #fafafa;
}

/* Product Info Column */
.col-product {
  display: flex;
  align-items: center;
}

.product-info {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  width: 100%;
}

.product-thumb {
  width: 60px;
  height: 60px;
  object-fit: cover;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  flex-shrink: 0;
}

.product-details {
  flex: 1;
  min-width: 0;
}

.brand {
  margin: 0;
  font-size: 12px;
  color: #888;
}

.product-name {
  margin: 2px 0;
  font-size: 14px;
  font-weight: 600;
  color: #005eb8;
  line-height: 1.3;
}

.product-name-link {
  margin: 2px 0;
  font-size: 14px;
  font-weight: 600;
  color: #005eb8;
  line-height: 1.3;
  text-decoration: none;
  cursor: pointer;
  display: block;
  transition: color 0.2s;
}

.product-name-link:hover {
  color: #004a94;
  text-decoration: underline;
}

.model {
  margin: 2px 0;
  font-size: 12px;
  color: #999;
}

.tag-inline {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 3px;
  font-size: 11px;
  font-weight: 600;
  margin-top: 4px;
}

.tag-inline.new {
  background: #e6f7e6;
  color: #2e7d32;
}

.tag-inline.hot {
  background: #fff0e6;
  color: #d84315;
}

/* Specs Column */
.col-specs {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 300px;
  overflow-y: auto;
}

.specs-list {
  list-style: none;
  padding: 0;
  margin: 0;
  font-size: 12px;
}

.specs-list li {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  padding: 2px 0;
}

.spec-label {
  font-weight: 600;
  color: #666;
}

.spec-value {
  color: #333;
  text-align: right;
}

.specs-more {
  margin: 4px 0 0 0;
  font-size: 11px;
  color: #005eb8;
  cursor: pointer;
}

/* Stock Column */
.col-stock {
  display: flex;
  align-items: center;
}

.stock-info {
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 12px;
  width: 100%;
}

.stock-count {
  color: #333;
  font-weight: 600;
}

.stock-status {
  padding: 4px 8px;
  border-radius: 3px;
  font-weight: 600;
  text-align: center;
  width: 100%;
}

.stock-status.status-in-stock {
  background: #e6f7e6;
  color: #2e7d32;
}

.stock-status.status-low-stock {
  background: #fff0e6;
  color: #d84315;
}

.stock-status.status-out-of-stock {
  background: #f0f0f0;
  color: #999;
}

.delivery-time {
  color: #666;
  font-size: 11px;
}

/* Action Column */
.col-action {
  display: flex;
  align-items: center;
}

.action-content {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
}

/* Action Buttons */
.action-buttons {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.btn-action {
  flex: 1;
  min-width: 70px;
  padding: 8px 12px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  border: 1px solid;
  transition: all 0.2s;
  text-align: center;
}

.btn-cart,
.btn-commission {
  background: #005eb8;
  color: white;
  border-color: #005eb8;
  flex: 1.2;
}

.btn-cart:hover,
.btn-commission:hover {
  background: #004a94;
  border-color: #004a94;
}

.btn-link {
  background: white;
  color: #005eb8;
  border-color: #d0d0d0;
}

.btn-link:hover {
  background: #f0f8ff;
  border-color: #005eb8;
}

/* Legacy button styles */
.btn-add-cart,
.btn-view-detail {
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid;
  transition: all 0.2s;
}

.btn-add-cart {
  background: #005eb8;
  color: white;
  border-color: #005eb8;
  padding: 10px 16px;
  font-size: 13px;
  font-weight: 600;
  flex: 1;
}

.btn-add-cart:hover {
  background: #004a94;
  border-color: #004a94;
}

.btn-view-detail {
  background: white;
  color: #005eb8;
  border-color: #005eb8;
}

.btn-view-detail:hover {
  background: #f0f8ff;
}

/* Extra Links */
.extra-links {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  padding-top: 4px;
  border-top: 1px solid #f0f0f0;
}

.link-detail,
.link-datasheet {
  color: #005eb8;
  text-decoration: none;
  transition: color 0.2s;
  cursor: pointer;
}

.link-detail:hover,
.link-datasheet:hover {
  color: #004a94;
  text-decoration: underline;
}

.link-divider {
  color: #ddd;
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 40px;
  color: #999;
}

/* Responsive */
@media (max-width: 1200px) {
  .product-list-header,
  .product-list-item {
    grid-template-columns: 1fr 1fr 0.8fr 0.8fr;
    gap: 12px;
  }

  .col-specs {
    font-size: 11px;
  }
}

@media (max-width: 768px) {
  .product-layout {
    flex-direction: column;
  }

  .sidebar {
    width: 100%;
    margin-bottom: 20px;
  }

  .product-list-header {
    grid-template-columns: 1fr 1fr 0.6fr;
    gap: 8px;
    padding: 12px;
  }

  .product-list-item {
    grid-template-columns: 1fr 1fr 0.6fr;
    gap: 8px;
    padding: 12px;
  }

  .col-stock {
    display: none;
  }

  .action-content {
    flex-direction: column;
    gap: 6px;
  }

  .action-buttons {
    gap: 4px;
  }

  .btn-action {
    padding: 6px 8px;
    font-size: 11px;
    min-width: auto;
  }

  .btn-cart {
    flex: 1.2;
  }

  .btn-add-cart,
  .btn-view-detail {
    padding: 6px 8px;
    font-size: 11px;
  }

  .extra-links {
    display: none;
  }

  .product-thumb {
    width: 50px;
    height: 50px;
  }

  .product-name {
    font-size: 12px;
  }
}
</style>
